import MESSAGES from '../common/data/constants/messages';
import { ICognito, ILogin, INewPassword } from '../common/data/dtos/auth';
import { GET_TYPED_ASYNC, POST_TYPED_ASYNC } from '../helpers/httpHelper';

const BASE_PATH = 'api/Auth';

interface IFirstAccessResponse {
    employeeId?: string;
    companyId?: string;
    firstAccess?: boolean;
    name?: string;
    error?: string;
}

export interface ILoginResponse {
    employeeId?: string;
    companyId?: string;
    firstAccess?: boolean;
    session?: string;
    challengeName?: string;
}

/**
 * Erro genérico retornado pelas operações da AWS
 */
export interface IAWSError {
    /** Nome do erro (por exemplo, 'NotAuthorizedException') */
    name: string;
    /** Mensagem legível */
    message: string;
    /** Código curto e único do erro */
    code: string;
    /** Região onde a requisição foi feita */
    region?: string;
    /** Timestamp do erro */
    time: Date;
    /** Request ID associada ao erro */
    requestId?: string;
    /** Se o erro é retryable */
    retryable?: boolean;
    /** Código HTTP retornado */
    statusCode?: number;
    /** Tempo de back-off antes de retry (ms) */
    retryDelay?: number;
}

/**
 * Detalhes brutos da resposta HTTP
 */
export interface IHttpResponse {
    /** Código de status HTTP */
    statusCode: number;
    /** Mensagem de status (por ex. "OK", "No Content") */
    statusMessage: string;
    /** Cabeçalhos retornados */
    headers: Record<string, string>;
    /** Corpo da resposta (string ou bytes) */
    body: string | Uint8Array;
}

/**
 * Payload específico de ChangePassword —
 * no Cognito, é sempre um objeto vazio, mas mantemos o tipo para consistência.
 */
export interface IChangePasswordData {
    // nenhum campo, payload vazio: {}
}

/**
 * IChangePasswordResponse
 *
 * Representa TUDO que a AWS retorna numa chamada cognitoidentityserviceprovider.changePassword()
 * sem usar tipos internos do SDK.
 */
export interface IChangePasswordResponse {
    /** Payload da operação (sempre um objeto vazio no ChangePassword) */
    data: IChangePasswordData;
    /** Erro ocorrido, ou null em caso de sucesso */
    error: IAWSError | null;
    /** Request ID única da AWS para rastreamento */
    requestId: string;
    /** Número de tentativas de retry que o SDK fez */
    retryCount: number;
    /** Número de redirecionamentos HTTP seguidos */
    redirectCount: number;
    /** Dados completos da resposta HTTP */
    httpResponse: IHttpResponse;
}

/**
 * IAdminSetUserPasswordData
 *
 * Payload específico de AdminSetUserPassword.
 * O método adminSetUserPassword do Cognito Identity Service Provider
 * retorna um objeto vazio, mas mantemos o tipo para consistência.
 */
export interface IAdminSetUserPasswordData {
    // payload vazio: {}
}

/**
 * IAdminSetUserPasswordResponse
 *
 * Representa TUDO que a AWS retorna numa chamada
 * cognitoidentityserviceprovider.adminSetUserPassword()
 * sem usar tipos internos do SDK.
 */
export interface IAdminSetUserPasswordResponse {
    /** Payload da operação (sempre um objeto vazio em AdminSetUserPassword) */
    data: IAdminSetUserPasswordData;
    /** Erro ocorrido, ou null em caso de sucesso */
    error: IAWSError | null;
    /** Request ID única da AWS para rastreamento */
    requestId: string;
    /** Número de tentativas de retry que o SDK fez */
    retryCount: number;
    /** Número de redirecionamentos HTTP seguidos */
    redirectCount: number;
    /** Dados completos da resposta HTTP */
    httpResponse: IHttpResponse;
}

/**
 * Metadados de um novo dispositivo (quando presentes)
 */
export interface INewDeviceMetadata {
    /** Chave única do dispositivo */
    deviceKey?: string;
    /** Chave de grupo do dispositivo */
    deviceGroupKey?: string;
}

/**
 * Resultado de autenticação retornado pelo Cognito em AdminInitiateAuth
 */
export interface IAuthenticationResult {
    /** Token de acesso JWT */
    accessToken?: string;
    /** Tempo de expiração em segundos */
    expiresIn?: number;
    /** Token de ID JWT */
    idToken?: string;
    /** Metadados de novo dispositivo */
    newDeviceMetadata?: INewDeviceMetadata;
    /** Token de refresh */
    refreshToken?: string;
    /** Tipo do token (por exemplo, "Bearer") */
    tokenType?: string;
}

/**
 * Payload específico de AdminInitiateAuth
 */
export interface IAdminInitiateAuthData {
    /** Resultado de autenticação, se já concluída */
    authenticationResult?: IAuthenticationResult;
    /**
     * Nome do desafio, se houver:
     * SMS_MFA | PASSWORD_VERIFIER | CUSTOM_CHALLENGE |
     * DEVICE_SRP_AUTH | DEVICE_PASSWORD_VERIFIER |
     * ADMIN_NO_SRP_AUTH | NEW_PASSWORD_REQUIRED | string
     */
    challengeName?: string;
    /** Parâmetros de desafio, se presentes */
    challengeParameters?: Record<string, string>;
    /** Session usada para chamadas subsequentes de desafio */
    session?: string;
}

/**
 * Representa TUDO que a AWS retorna numa chamada
 * cognitoidentityserviceprovider.adminInitiateAuth()
 * sem usar tipos internos do SDK.
 */
export interface IAdminInitiateAuthResponse {
    /** Payload da operação */
    data: IAdminInitiateAuthData;
    /** Erro ocorrido, ou null em caso de sucesso */
    error: IAWSError | null;
    /** Request ID única da AWS para rastreamento */
    requestId: string;
    /** Número de tentativas de retry que o SDK fez */
    retryCount: number;
    /** Número de redirecionamentos HTTP seguidos */
    redirectCount: number;
    /** Dados completos da resposta HTTP */
    httpResponse: IHttpResponse;
}

interface ISendTokenRequest {
    email: string;
    otp: string;
    session: string;
}

interface ISendTokenResponse {
    accessToken?: string;
    idToken?: string;
    expiresIn?: number;
    refreshToken?: string;
}

const checkFirstAccess = async (email: string) : Promise<IBaseObject<IFirstAccessResponse>> => {
    try {
        const response = await GET_TYPED_ASYNC<IFirstAccessResponse>(`${BASE_PATH}/CheckFirstAccess?email=${email}`);
        if (!response.success) {
            return { success: response.success, message: response.message, exception: response.exception };
        }

        return { success: response.success, entity: response.entity, message: response.message, exception: response.exception };

    } catch (err: any) {
        return { success: false, message: err.message, exception: err.exception };
    }
};

const doLogin = async (login: ILogin): Promise<IBaseObject<ILoginResponse>> => {
    try {
        const response = await POST_TYPED_ASYNC<ILoginResponse>(`${BASE_PATH}/Login`, login);

        return { success: response.success, entity: response.entity, message: response.message, exception: response.exception };
    } catch (err: any) {
        return { success: false, message: err.message };
    }
};

const updatePassword = async (newPassword: INewPassword): Promise<IBaseObject<IChangePasswordResponse>> => {
    try {
        if (newPassword.password !== newPassword.confirmPassword) {
            return { success: false, exception: '', message: MESSAGES.VALIDATION.NOT_MATCHING_PASSWORD }
        }

        const response = await POST_TYPED_ASYNC<IChangePasswordResponse>(`${BASE_PATH}/UpdatePassword`, newPassword);

        return response;
    } catch (err: any) {
        return { success: false, exception: err, message: err.message };
    }
};

const assignPassword = async (newPassword: ICognito): Promise<IBaseObject<IAdminSetUserPasswordResponse>> => {
    try {
        if (newPassword.password !== newPassword.oldPassword) {
            return { success: false, exception: '', message: MESSAGES.VALIDATION.NOT_MATCHING_PASSWORD }
        }
        const response = await POST_TYPED_ASYNC<IAdminSetUserPasswordResponse>(`${BASE_PATH}/AssignPassword`, newPassword);
        return response;
    } catch (err: any) {
        return { success: false, exception: err, message: err.message };
    }
}

const createCognitoUser = async (email: string, password: string): Promise<IBaseObject<ICognito>> => {
    try {
        const response = await POST_TYPED_ASYNC<ICognito>(`${BASE_PATH}/CreateCognitoUser`, { email, password });
        return response;
    } catch (err: any) {
        return { success: false, exception: err, message: err.message };
    }
}

const sendToken = async (request: ISendTokenRequest): Promise<IBaseObject<ISendTokenResponse>> => {
    try {
        const response = await POST_TYPED_ASYNC<ISendTokenResponse>(`${BASE_PATH}/SendToken`, request);
        return response;
    } catch (err: any) {
        return { success: false, exception: err, message: err.message };
    }
}

const resendConfirmationCode = async (email: string): Promise<IBaseObject<ISendTokenResponse>> => {
    try {
        const response = await POST_TYPED_ASYNC<ISendTokenResponse>(`${BASE_PATH}/ResendConfirmationCode`, { email });
        return response;
    } catch (err: any) {
        return { success: false, exception: err, message: err.message };
    }
}

export default {
    doLogin,
    checkFirstAccess,
    updatePassword,
    assignPassword,
    createCognitoUser,
    sendToken,
    resendConfirmationCode
};
